/**
 * Activity Tracker with Multi-Tab Sync
 * Enhanced version that synchronizes activity across browser tabs
 */

import { broadcastActivity } from './tabSync';
import logger from './logger';

const LAST_ACTIVITY_KEY = 'lastUserActivity';
const CONFIG_KEY = 'sessionConfig';
const CONFIG_FETCH_TIME_KEY = 'sessionConfigFetchTime';
const CONFIG_CACHE_DURATION = 60 * 60 * 1000;

let INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // Default 30 minutes
let CURRENT_CHANNEL_NAME = 'digitalvault-session'; // Default channel
let activityListeners = [];
let isTracking = false;

// Feature flag for tab sync
const TAB_SYNC_ENABLED = import.meta.env.VITE_ENABLE_TAB_SYNC !== 'false';

/**
 * Fetch session configuration from backend
 */
export const fetchSessionConfig = async () => {
  try {
    const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || '';
    const cachedConfig = localStorage.getItem(CONFIG_KEY);
    const fetchTime = localStorage.getItem(CONFIG_FETCH_TIME_KEY);
    
    if (cachedConfig && fetchTime) {
      const age = Date.now() - parseInt(fetchTime, 10);
      if (age < CONFIG_CACHE_DURATION) {
        const config = JSON.parse(cachedConfig);
        INACTIVITY_TIMEOUT_MS = config.inactivityTimeoutMinutes * 60 * 1000;
        return config;
      }
    }

    const response = await fetch(`${BACKEND_URL}/api/config/session`);
    if (response.ok) {
      const config = await response.json();
      INACTIVITY_TIMEOUT_MS = config.inactivityTimeoutMinutes * 60 * 1000;
      localStorage.setItem(CONFIG_KEY, JSON.stringify(config));
      localStorage.setItem(CONFIG_FETCH_TIME_KEY, Date.now().toString());
      return config;
    }
  } catch (error) {
    // Silently fail - will use defaults
  }
  
  return {
    inactivityTimeoutMinutes: 30,
    tokenExpirationHours: 24,
    refreshThresholdHours: 2
  };
};

export const getInactivityTimeout = () => INACTIVITY_TIMEOUT_MS;

/**
 * Set inactivity timeout for specific role
 * @param {number} minutes - Timeout in minutes
 */
export const setInactivityTimeout = (minutes) => {
  INACTIVITY_TIMEOUT_MS = minutes * 60 * 1000;
  logger.log(`[ActivityTracker] Inactivity timeout set to ${minutes} minutes`);
};

/**
 * Set channel name for broadcasting
 * @param {string} channelName - BroadcastChannel name
 */
export const setChannelName = (channelName) => {
  CURRENT_CHANNEL_NAME = channelName;
  logger.log(`[ActivityTracker] Channel name set to ${channelName}`);
};

/**
 * Update last activity timestamp
 * @param {boolean} broadcast - Whether to broadcast to other tabs
 */
const updateActivity = (broadcast = true) => {
  const now = Date.now();
  localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());

  // Broadcast to other tabs if enabled
  if (broadcast && TAB_SYNC_ENABLED) {
    broadcastActivity({ timestamp: now }, CURRENT_CHANNEL_NAME);
  }
};

export const getLastActivity = () => {
  const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
  return lastActivity ? parseInt(lastActivity, 10) : Date.now();
};

export const isUserInactive = () => {
  const lastActivity = getLastActivity();
  const now = Date.now();
  const inactiveDuration = now - lastActivity;
  return inactiveDuration >= INACTIVITY_TIMEOUT_MS;
};

export const getTimeUntilInactive = () => {
  const lastActivity = getLastActivity();
  const now = Date.now();
  const inactiveDuration = now - lastActivity;
  const remaining = INACTIVITY_TIMEOUT_MS - inactiveDuration;
  return Math.max(0, remaining);
};

/**
 * Handle activity from another tab
 * Called by tab sync when another tab is active
 */
export const handleRemoteActivity = (data) => {
  // Update activity without broadcasting (avoid infinite loop)
  updateActivity(false);
};

/**
 * Start activity tracking
 * @param {number} timeoutMinutes - Inactivity timeout in minutes (default: 30)
 * @param {string} channelName - BroadcastChannel name for sync (default: digitalvault-session)
 */
export const startActivityTracking = async (timeoutMinutes = 30, channelName = 'digitalvault-session') => {
  if (isTracking) return;

  // Set timeout and channel name
  setInactivityTimeout(timeoutMinutes);
  setChannelName(channelName);

  updateActivity(true); // Initial update with broadcast

  const events = [
    'mousedown',
    'mousemove',
    'keydown',
    'scroll',
    'touchstart',
    'click'
  ];

  let throttleTimer = null;
  const throttledUpdate = () => {
    if (!throttleTimer) {
      updateActivity(true); // Update with broadcast
      throttleTimer = setTimeout(() => {
        throttleTimer = null;
      }, 1000);
    }
  };

  events.forEach(event => {
    window.addEventListener(event, throttledUpdate, true);
    activityListeners.push({ event, handler: throttledUpdate });
  });

  isTracking = true;
  const syncStatus = TAB_SYNC_ENABLED ? `with tab sync (${channelName})` : 'without tab sync';
  logger.log(`[ActivityTracker] Tracking started ${syncStatus} (${timeoutMinutes} min timeout)`);
};

export const stopActivityTracking = () => {
  if (!isTracking) return;

  activityListeners.forEach(({ event, handler }) => {
    window.removeEventListener(event, handler, true);
  });

  activityListeners = [];
  isTracking = false;
  localStorage.removeItem(LAST_ACTIVITY_KEY);
};

export const resetActivity = (broadcast = true) => {
  updateActivity(broadcast);
};
